import React, { useState, useEffect } from 'react';
import announcementService, { PlatformAnnouncement, CreateAnnouncementData } from '../../services/announcementService';
import {
  SpeakerWaveIcon,
  PlusIcon,
  PencilIcon,
  TrashIcon,
  ArrowPathIcon,
  InformationCircleIcon,
  ExclamationTriangleIcon,
  WrenchScrewdriverIcon,
  SparklesIcon,
  ShieldExclamationIcon,
} from '@heroicons/react/24/outline';

const AdminAnnouncementsPage: React.FC = () => {
  const [announcements, setAnnouncements] = useState<PlatformAnnouncement[]>([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingAnnouncement, setEditingAnnouncement] = useState<PlatformAnnouncement | null>(null);
  
  const [formData, setFormData] = useState<CreateAnnouncementData>({
    title: '',
    message: '',
    type: 'INFO',
    targetAudience: 'ALL',
    isActive: true,
    startDate: new Date().toISOString().split('T')[0],
    dismissible: true,
  });

  useEffect(() => {
    loadAnnouncements();
  }, []);

  const loadAnnouncements = async () => {
    try {
      setLoading(true);
      const data = await announcementService.getAll();
      setAnnouncements(data.sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()));
    } catch (error) {
      console.error('Failed to load announcements:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      if (editingAnnouncement) {
        await announcementService.update(editingAnnouncement.id, formData);
      } else {
        await announcementService.create(formData);
      }
      setShowModal(false);
      resetForm();
      loadAnnouncements();
    } catch (error) {
      console.error('Failed to save announcement:', error);
      alert('Failed to save announcement. Please try again.');
    }
  };

  const handleDelete = async (id: string) => {
    if (!confirm('Are you sure you want to delete this announcement?')) return;
    try {
      await announcementService.delete(id);
      loadAnnouncements();
    } catch (error) {
      console.error('Failed to delete announcement:', error);
      alert('Failed to delete announcement.');
    }
  };

  const handleToggle = async (id: string) => {
    try {
      await announcementService.toggle(id);
      loadAnnouncements();
    } catch (error) {
      console.error('Failed to toggle announcement:', error);
    }
  };

  const openCreateModal = () => {
    resetForm();
    setEditingAnnouncement(null);
    setShowModal(true);
  };

  const openEditModal = (announcement: PlatformAnnouncement) => {
    setEditingAnnouncement(announcement);
    setFormData({
      title: announcement.title,
      message: announcement.message,
      type: announcement.type,
      targetAudience: announcement.targetAudience,
      isActive: announcement.isActive,
      startDate: new Date(announcement.startDate).toISOString().split('T')[0],
      endDate: announcement.endDate ? new Date(announcement.endDate).toISOString().split('T')[0] : undefined,
      dismissible: announcement.dismissible,
      actionUrl: announcement.actionUrl,
      actionText: announcement.actionText,
    });
    setShowModal(true);
  };

  const resetForm = () => {
    setFormData({
      title: '',
      message: '',
      type: 'INFO',
      targetAudience: 'ALL',
      isActive: true,
      startDate: new Date().toISOString().split('T')[0],
      dismissible: true,
    });
  };

  const getTypeIcon = (type: string) => {
    const icons: Record<string, { Icon: any; color: string }> = {
      INFO: { Icon: InformationCircleIcon, color: 'text-blue-600' },
      WARNING: { Icon: ExclamationTriangleIcon, color: 'text-yellow-600' },
      MAINTENANCE: { Icon: WrenchScrewdriverIcon, color: 'text-orange-600' },
      FEATURE: { Icon: SparklesIcon, color: 'text-purple-600' },
      CRITICAL: { Icon: ShieldExclamationIcon, color: 'text-red-600' },
    };
    const { Icon, color } = icons[type] || icons.INFO;
    return <Icon className={`h-6 w-6 ${color}`} />;
  };

  const getTypeBadge = (type: string) => {
    const badges: Record<string, string> = {
      INFO: 'bg-blue-100 text-blue-800',
      WARNING: 'bg-yellow-100 text-yellow-800',
      MAINTENANCE: 'bg-orange-100 text-orange-800',
      FEATURE: 'bg-purple-100 text-purple-800',
      CRITICAL: 'bg-red-100 text-red-800',
    };
    return badges[type] || badges.INFO;
  };

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center justify-between">
            <div className="flex items-center">
              <SpeakerWaveIcon className="h-10 w-10 text-indigo-600 mr-3" />
              <div>
                <h1 className="text-3xl font-bold text-gray-900">Platform Announcements</h1>
                <p className="text-gray-600 mt-1">Create and manage system-wide notifications</p>
              </div>
            </div>
            <div className="flex gap-3">
              <button
                onClick={loadAnnouncements}
                className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
              >
                <ArrowPathIcon className="h-4 w-4 mr-2" />
                Refresh
              </button>
              <button
                onClick={openCreateModal}
                className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700"
              >
                <PlusIcon className="h-4 w-4 mr-2" />
                New Announcement
              </button>
            </div>
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
          <div className="bg-white rounded-lg shadow p-6">
            <p className="text-sm font-medium text-gray-600">Total</p>
            <p className="text-2xl font-bold text-gray-900">{announcements.length}</p>
          </div>
          <div className="bg-white rounded-lg shadow p-6">
            <p className="text-sm font-medium text-gray-600">Active</p>
            <p className="text-2xl font-bold text-green-600">
              {announcements.filter(a => a.isActive).length}
            </p>
          </div>
          <div className="bg-white rounded-lg shadow p-6">
            <p className="text-sm font-medium text-gray-600">Critical</p>
            <p className="text-2xl font-bold text-red-600">
              {announcements.filter(a => a.type === 'CRITICAL').length}
            </p>
          </div>
          <div className="bg-white rounded-lg shadow p-6">
            <p className="text-sm font-medium text-gray-600">Maintenance</p>
            <p className="text-2xl font-bold text-orange-600">
              {announcements.filter(a => a.type === 'MAINTENANCE').length}
            </p>
          </div>
        </div>

        {/* Announcements List */}
        <div className="space-y-4">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <ArrowPathIcon className="h-8 w-8 text-gray-400 animate-spin" />
              <span className="ml-2 text-gray-600">Loading announcements...</span>
            </div>
          ) : announcements.length === 0 ? (
            <div className="text-center py-12 bg-white rounded-lg shadow">
              <SpeakerWaveIcon className="mx-auto h-12 w-12 text-gray-400" />
              <h3 className="mt-2 text-sm font-medium text-gray-900">No announcements</h3>
              <p className="mt-1 text-sm text-gray-500">Create your first announcement</p>
            </div>
          ) : (
            announcements.map((announcement) => (
              <div
                key={announcement.id}
                className={`bg-white rounded-lg shadow p-6 ${!announcement.isActive ? 'opacity-50' : ''}`}
              >
                <div className="flex items-start justify-between">
                  <div className="flex items-start space-x-4 flex-1">
                    <div className="flex-shrink-0">
                      {getTypeIcon(announcement.type)}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-2">
                        <h3 className="text-lg font-semibold text-gray-900">{announcement.title}</h3>
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getTypeBadge(announcement.type)}`}>
                          {announcement.type}
                        </span>
                        <span className="text-xs text-gray-500">
                          Target: {announcement.targetAudience}
                        </span>
                      </div>
                      <p className="text-sm text-gray-700 whitespace-pre-wrap">{announcement.message}</p>
                      <div className="mt-3 flex items-center gap-4 text-xs text-gray-500">
                        <span>Start: {new Date(announcement.startDate).toLocaleDateString()}</span>
                        {announcement.endDate && (
                          <span>End: {new Date(announcement.endDate).toLocaleDateString()}</span>
                        )}
                        {announcement.dismissible && <span>Dismissible</span>}
                        {announcement.actionUrl && <span>Has action button</span>}
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 ml-4">
                    <button
                      onClick={() => handleToggle(announcement.id)}
                      className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none ${
                        announcement.isActive ? 'bg-indigo-600' : 'bg-gray-200'
                      }`}
                    >
                      <span
                        className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                          announcement.isActive ? 'translate-x-5' : 'translate-x-0'
                        }`}
                      />
                    </button>
                    <button
                      onClick={() => openEditModal(announcement)}
                      className="text-blue-600 hover:text-blue-900"
                    >
                      <PencilIcon className="h-5 w-5" />
                    </button>
                    <button
                      onClick={() => handleDelete(announcement.id)}
                      className="text-red-600 hover:text-red-900"
                    >
                      <TrashIcon className="h-5 w-5" />
                    </button>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>

        {/* Create/Edit Modal */}
        {showModal && (
          <div className="fixed inset-0 z-50 overflow-y-auto">
            <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20">
              <div className="fixed inset-0 bg-gray-500 bg-opacity-75" onClick={() => setShowModal(false)} />
              
              <div className="inline-block bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:max-w-2xl sm:w-full">
                <form onSubmit={handleSubmit}>
                  <div className="bg-white px-4 pt-5 pb-4 sm:p-6">
                    <h3 className="text-lg font-medium text-gray-900 mb-4">
                      {editingAnnouncement ? 'Edit Announcement' : 'Create Announcement'}
                    </h3>
                    
                    <div className="space-y-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Title *</label>
                        <input
                          type="text"
                          required
                          value={formData.title}
                          onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-indigo-500"
                          placeholder="Announcement title"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Message *</label>
                        <textarea
                          required
                          value={formData.message}
                          onChange={(e) => setFormData(prev => ({ ...prev, message: e.target.value }))}
                          rows={4}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-indigo-500"
                          placeholder="Announcement message"
                        />
                      </div>

                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">Type *</label>
                          <select
                            value={formData.type}
                            onChange={(e) => setFormData(prev => ({ ...prev, type: e.target.value as any }))}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-indigo-500"
                          >
                            <option value="INFO">Info</option>
                            <option value="WARNING">Warning</option>
                            <option value="MAINTENANCE">Maintenance</option>
                            <option value="FEATURE">New Feature</option>
                            <option value="CRITICAL">Critical</option>
                          </select>
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">Target Audience *</label>
                          <select
                            value={formData.targetAudience}
                            onChange={(e) => setFormData(prev => ({ ...prev, targetAudience: e.target.value as any }))}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-indigo-500"
                          >
                            <option value="ALL">All Users</option>
                            <option value="CREATORS">Creators Only</option>
                            <option value="PATRONS">Patrons Only</option>
                            <option value="ADMINS">Admins Only</option>
                          </select>
                        </div>
                      </div>

                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">Start Date *</label>
                          <input
                            type="date"
                            required
                            value={formData.startDate}
                            onChange={(e) => setFormData(prev => ({ ...prev, startDate: e.target.value }))}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-indigo-500"
                          />
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">End Date (Optional)</label>
                          <input
                            type="date"
                            value={formData.endDate || ''}
                            onChange={(e) => setFormData(prev => ({ ...prev, endDate: e.target.value }))}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-indigo-500"
                          />
                        </div>
                      </div>

                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">Action URL (Optional)</label>
                          <input
                            type="url"
                            value={formData.actionUrl || ''}
                            onChange={(e) => setFormData(prev => ({ ...prev, actionUrl: e.target.value }))}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-indigo-500"
                            placeholder="https://..."
                          />
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">Action Text (Optional)</label>
                          <input
                            type="text"
                            value={formData.actionText || ''}
                            onChange={(e) => setFormData(prev => ({ ...prev, actionText: e.target.value }))}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-indigo-500"
                            placeholder="Learn More"
                          />
                        </div>
                      </div>

                      <div className="flex gap-4">
                        <label className="flex items-center text-sm">
                          <input
                            type="checkbox"
                            checked={formData.isActive}
                            onChange={(e) => setFormData(prev => ({ ...prev, isActive: e.target.checked }))}
                            className="rounded border-gray-300 text-indigo-600 focus:ring-indigo-500 mr-2"
                          />
                          <span>Active immediately</span>
                        </label>
                        <label className="flex items-center text-sm">
                          <input
                            type="checkbox"
                            checked={formData.dismissible}
                            onChange={(e) => setFormData(prev => ({ ...prev, dismissible: e.target.checked }))}
                            className="rounded border-gray-300 text-indigo-600 focus:ring-indigo-500 mr-2"
                          />
                          <span>User can dismiss</span>
                        </label>
                      </div>
                    </div>
                  </div>
                  
                  <div className="bg-gray-50 px-4 py-3 sm:px-6 flex justify-end gap-2">
                    <button
                      type="submit"
                      className="px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700"
                    >
                      {editingAnnouncement ? 'Update' : 'Create'}
                    </button>
                    <button
                      type="button"
                      onClick={() => setShowModal(false)}
                      className="px-4 py-2 border border-gray-300 rounded-md hover:bg-gray-50"
                    >
                      Cancel
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminAnnouncementsPage;
